// base service class following SRP, SOC, and DRY principles

import { BaseService } from "./BaseService";
import {
  ServiceResult,
  ServiceError,
  ServiceOperationContext,
  ValidationContext,
  PaginationParams,
  ServiceConfig,
  DEFAULT_SERVICE_CONFIG,
  IDomainService,
} from "./service-types";

/**
 * base service that provides common functionality for all domain services
 * Follows SRP: Single responsibility for service infrastructure
 * Follows SOC: Separates service concerns from business logic
 * Follows DRY: Common patterns abstracted to base class
 */
export abstract class AppBaseService
  extends BaseService
  implements IDomainService
{
  protected config: ServiceConfig;

  constructor(config?: Partial<ServiceConfig>) {
    super();
    this.config = { ...DEFAULT_SERVICE_CONFIG, ...config };
  }

  /**
   * Abstract property that each service must implement
   */
  abstract readonly serviceName: string;

  /**
   * Execute operation with comprehensive error handling and logging
   * Follows DRY: Centralized error handling pattern
   */
  protected async executeWithErrorHandling<T>(
    operation: () => Promise<T>,
    context: ServiceOperationContext
  ): Promise<ServiceResult<T>> {
    const startTime = Date.now();

    try {
      if (this.config.enableLogging) {
        this.logServiceOperation("START", context);
      }

      const result = await operation();

      if (this.config.enableLogging) {
        const duration = Date.now() - startTime;
        this.logServiceOperation("SUCCESS", context, { duration });
      }

      return this.createSuccessResult(result);
    } catch (error) {
      const duration = Date.now() - startTime;
      const enhancedError = this.createError(error, context);

      if (this.config.enableLogging) {
        this.logServiceOperation("ERROR", context, {
          duration,
          error: enhancedError,
        });
      }

      return this.createErrorResult(enhancedError);
    }
  }

  /**
   * Execute operation with retry logic
   * Follows DRY: Centralized retry pattern
   */
  protected async executeWithRetry<T>(
    operation: () => Promise<T>,
    context: ServiceOperationContext,
    retries?: number
  ): Promise<ServiceResult<T>> {
    const maxRetries = retries ?? this.config.retries ?? 3;
    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await operation();
        return this.createSuccessResult(result);
      } catch (error) {
        lastError = error;

        // Don't retry on client errors (4xx) or last attempt
        if (this.isClientError(error) || attempt === maxRetries) {
          break;
        }

        // Wait before retry with exponential backoff
        await this.delay(Math.pow(2, attempt) * 1000);
      }
    }

    const enhancedError = this.createError(lastError, context);
    return this.createErrorResult(enhancedError);
  }

  /**
   * Validate required fields
   * Follows SRP: Single responsibility for validation
   */
  protected validateRequired(
    data: Record<string, unknown>,
    required: string[],
    context?: ValidationContext
  ): void {
    if (!this.config.enableValidation) {
      return;
    }

    const missing = required.filter(
      field =>
        data[field] === undefined || data[field] === null || data[field] === ""
    );

    if (missing.length > 0) {
      const error: ServiceError = {
        message: `Missing required fields: ${missing.join(", ")}`,
        code: "VALIDATION_ERROR",
        validationErrors: missing.map(field => ({
          field,
          message: `${field} is required`,
          code: "REQUIRED",
        })),
        context: context
          ? {
              operation: context.operation,
              entity: context.entity,
              metadata: { field: context.field },
            }
          : undefined,
      };
      throw error;
    }
  }

  /**
   * Create standardized pagination parameters
   * Follows DRY: Centralized pagination logic
   */
  protected createPaginationParams(
    skip?: number,
    limit?: number
  ): PaginationParams {
    return {
      skip: skip ?? 0,
      limit: limit ?? 50,
      offset: skip ?? 0,
      page: skip && limit ? Math.floor(skip / limit) + 1 : 1,
    };
  }

  /**
   * Log service operations
   * Follows SRP: Single responsibility for logging
   */
  protected logServiceOperation(
    stage: "START" | "SUCCESS" | "ERROR",
    context: ServiceOperationContext,
    metadata?: Record<string, unknown>
  ): void {
    const logData = {
      service: this.serviceName,
      operation: context.operation,
      entity: context.entity,
      entityId: context.entityId,
      userId: context.userId,
      stage,
      timestamp: new Date().toISOString(),
      ...metadata,
    };

    if (stage === "ERROR") {
      // Include more error details for debugging
      const errorDetails = metadata?.error as ServiceError;
      if (errorDetails) {
        // Check if this is an expected 404 error for RAG configuration operations
        const isRagConfigOperation = context.operation === "getAgentRagConfig";

        // Check for 404 status in multiple locations
        const is404Error = this.is404Error(errorDetails);

        const isExpectedRagConfigNotFound = isRagConfigOperation && is404Error;

        // Suppress noisy logs for expected 403 on superadmin-only endpoints when callers handle fallbacks
        const isGetOrganizations = context.operation === "getOrganizations";
        const is403Error = this.is403Error(errorDetails);
        const isExpectedOrgListForbidden = isGetOrganizations && is403Error;

        // Use debug logging for expected RAG config not found errors, error logging for others
        if (isExpectedRagConfigNotFound || isExpectedOrgListForbidden) {
          // Silent handling for expected RAG config not found - no console output
          return;
        } else {
          console.error(`[${this.serviceName}] ${context.operation} failed:`, {
            ...logData,
            errorMessage: errorDetails.message,
            errorCode: errorDetails.code,
            httpStatus: errorDetails.httpStatus,
            errorDetails: errorDetails.details,
            // Only include originalError in development for debugging
            ...(process.env.NODE_ENV !== "production" && {
              originalError: errorDetails.originalError,
            }),
          });
        }
      } else {
        console.error(
          `[${this.serviceName}] ${context.operation} failed:`,
          logData
        );
      }
    } else if (process.env.NODE_ENV !== "production") {
    }
  }

  /**
   * Create enhanced success result
   */
  protected createSuccessResult<T>(data: T): ServiceResult<T> {
    return {
      success: true,
      data,
    };
  }

  /**
   * Create enhanced error result
   */
  protected createErrorResult<T>(error: ServiceError): ServiceResult<T> {
    return {
      success: false,
      error,
    };
  }

  /**
   * Create enhanced error from unknown error
   */
  protected createError(
    error: unknown,
    context: ServiceOperationContext
  ): ServiceError {
    const baseError = this.handleError(error, context.operation);

    const enhancedError: ServiceError = {
      ...baseError,
      context,
      httpStatus: this.extractHttpStatus(error),
    };

    // Extract validation errors if available
    if (this.isValidationError(error)) {
      enhancedError.validationErrors = this.extractValidationErrors(error);
    }

    return enhancedError;
  }

  /**
   * Check if error is a client error (4xx)
   */
  private isClientError(error: unknown): boolean {
    const status = this.extractHttpStatus(error);
    return status !== undefined && status >= 400 && status < 500;
  }

  /**
   * Check if error is a 404 Not Found error
   * Checks multiple locations where 404 status might be stored
   */
  private is404Error(errorDetails: ServiceError): boolean {
    // Check direct httpStatus property
    if (errorDetails.httpStatus === 404) {
      return true;
    }

    // Check in details object
    if (errorDetails.details?.httpStatus === 404) {
      return true;
    }

    // Check in originalError if it exists
    if (errorDetails.originalError) {
      const originalStatus = this.extractHttpStatus(errorDetails.originalError);
      if (originalStatus === 404) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if error is a 403 Forbidden error
   * Checks multiple locations where 403 status might be stored
   */
  private is403Error(errorDetails: ServiceError): boolean {
    if (errorDetails.httpStatus === 403) return true;
    if (errorDetails.details?.httpStatus === 403) return true;
    if (errorDetails.originalError) {
      const originalStatus = this.extractHttpStatus(errorDetails.originalError);
      if (originalStatus === 403) return true;
    }
    return false;
  }

  /**
   * Check if error is a validation error
   */
  private isValidationError(error: unknown): boolean {
    if (error && typeof error === "object") {
      const errorObj = error as Record<string, unknown>;
      return !!(errorObj.validationErrors || errorObj.details);
    }
    return false;
  }

  /**
   * Extract HTTP status from error
   */
  private extractHttpStatus(error: unknown): number | undefined {
    if (error && typeof error === "object") {
      const errorObj = error as Record<string, unknown>;

      // Check for axios error structure first
      if (errorObj.response && typeof errorObj.response === "object") {
        const response = errorObj.response as Record<string, unknown>;
        if (typeof response.status === "number") {
          return response.status;
        }
      }

      // Fallback to direct status properties
      return (errorObj.status as number) || (errorObj.statusCode as number);
    }
    return undefined;
  }

  /**
   * Extract validation errors from error object
   */
  private extractValidationErrors(
    error: unknown
  ): Array<{ field: string; message: string; code?: string }> {
    if (error && typeof error === "object") {
      const errorObj = error as Record<string, unknown>;

      if (Array.isArray(errorObj.validationErrors)) {
        return errorObj.validationErrors;
      }

      if (errorObj.details && typeof errorObj.details === "object") {
        const details = errorObj.details as Record<string, string>;
        return Object.entries(details).map(([field, message]) => ({
          field,
          message,
          code: "VALIDATION_ERROR",
        }));
      }
    }

    return [];
  }

  /**
   * Delay utility for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
